import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { api } from '@consts/url.const';
import { ZendeskArticleModel } from '@models/zendesk-article.model';
import { BYPASS_AUTHENTICAION_TOKEN } from 'src/app/core/authentication-interceptor.service';

@Injectable({ providedIn: 'root' })
export class ArticlesService {
	private readonly articlesUrl = '/assets/help/articles.json';

	constructor(private readonly http: HttpClient) { }

	// getArticles(): Observable<ZendeskArticleModel.ZendeskArticleRaw[]> {
	// 	return this.http.get<ZendeskArticleModel.ZendeskArticleRaw[]>(api.support.articlesCdn, {
	// 		context: new HttpContext().set(BYPASS_AUTHENTICAION_TOKEN, true)
	// 	});
	// }

	getArticles(): Observable<ZendeskArticleModel.ZendeskArticleRaw[]> {
		return this.http.get<ZendeskArticleModel.ZendeskArticleRaw[]>(api.support.articles);
	}

	fetchArticles(): Observable<ZendeskArticleModel.ArticleItem[]> {
		return this.http
			.get<ZendeskArticleModel.ZendeskArticleRaw[]>(api.support.articles)
			.pipe(
				map((response) => {
					const articles = response;
					const mappedArticles = articles.map((article) => {
						return this.mapArticle(article);
					});
					return mappedArticles;
				})
			);
	}

	private mapArticle(raw: ZendeskArticleModel.ZendeskArticleRaw): ZendeskArticleModel.ArticleItem {
		const category = (raw.label_names && raw.label_names.length)
			? raw.label_names.join(', ')
			: undefined; // don't expose numeric section id
		const rawBodyHtml = raw.body ?? '';
		const youtubeIds = this.extractYoutubeIds(rawBodyHtml);
		// Remove YouTube links from bodyHtml since we display them separately
		let bodyHtml = this.stripYoutubeLinks(rawBodyHtml);
		// Replace Zendesk attachment URLs with our proxy endpoint
		bodyHtml = this.proxyImageUrls(bodyHtml);
		return {
			id: raw.id,
			title: raw.title ?? raw.name ?? 'Untitled',
			htmlUrl: raw.html_url,
			bodyHtml,
			createdAt: raw.created_at,
			updatedAt: raw.updated_at,
			category,
			youtubeIds,
			youtubeEmbedUrls: youtubeIds.map(id => `https://www.youtube.com/embed/${id}?rel=0`),
		};
	}

	private extractYoutubeIds(html: string): string[] {
		if (!html) return [];
		const ids = new Set<string>();
		const patterns = [/(?:v=|\/embed\/|youtu\.be\/)([A-Za-z0-9_-]{11})/g];
		for (const re of patterns) {
			let m: RegExpExecArray | null;
			while ((m = re.exec(html)) !== null) {
				ids.add(m[1]);
			}
		}
		return [...ids];
	}

	private stripYoutubeLinks(html: string): string {
		if (!html) return '';
		// Remove YouTube anchor tags
		html = html.replace(/<a[^>]+href=["']https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)[^"']*["'][^>]*>.*?<\/a>/gi, '');
		// Remove plain YouTube URLs
		html = html.replace(/https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\S+/gi, '');
		return html;
	}

	/**
	 * Replace Zendesk image URLs with our backend proxy endpoint
	 * This allows images to be displayed without authentication issues
	 */
	private proxyImageUrls(html: string): string {
		if (!html) return '';

		// Match Zendesk attachment URLs in img tags
		// Pattern: https://support.lumana.ai/hc/article_attachments/{attachmentId}
		const pattern = /https:\/\/[^\/]+\.zendesk\.com\/hc\/article_attachments\/(\d+)/gi;

		return html.replace(pattern, (match, attachmentId) => {
			return `${api.support.articles.replace('/articles', '')}/article-attachments/${attachmentId}`;
		});
	}
}
